import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';

import {
  getReturnedRowIDsAndAmounts,
  getOrders,
} from 'reducers/returnProducts';
import { isRowFullyReturned, nestGroupedProducts } from 'utils';
import useProducts from 'utils/hooks/useProducts';
import { useAppDispatch } from 'reducers';
import { setOrdersSelected } from 'actions/returnProducts';

export function useReturnRows() {
  const dispatch = useAppDispatch();

  const returnedRows = useSelector(getReturnedRowIDsAndAmounts);
  const allRows = useSelector(getOrders);

  const { products } = useProducts({
    productIDs: allRows?.map(row => Number(row.productID)),
  });

  const getIsRowDisabled = useCallback(
    (row: ReturnType<typeof getOrders>[number]) => {
      const productCard = products.find(
        prod => String(row.productID) === String(prod.productID),
      );
      const isRefundable = productCard?.nonRefundable !== 1;
      const isFullyReturned = isRowFullyReturned(row, returnedRows);
      const hasPrice = !Number.isNaN(productCard?.price);
      const isNonStock = productCard?.nonStockProduct;

      return (
        !isRefundable ||
        isFullyReturned ||
        !(hasPrice || isNonStock) ||
        !row.remainingAmount
      );
    },
    [products, returnedRows],
  );

  // Used only for display
  const computedRows = useMemo(
    () =>
      // Link up products based on parentRowID
      nestGroupedProducts(
        allRows.map(row => ({ ...row, isReturnable: !getIsRowDisabled(row) })),
        {
          parentIndicator: 'parentRowID',
          idIndicator: 'stableRowID',
        },
      ),
    [allRows, getIsRowDisabled],
  );

  const returnableRows = allRows.filter(
    r => r.remainingAmount > 0 && !getIsRowDisabled(r),
  );
  const areAllSelected =
    returnableRows.length > 0 && returnableRows.every(r => r?.selected);

  // toggle on/off all rows in the return document
  function toggleAllRows() {
    if (areAllSelected) {
      dispatch(setOrdersSelected({}));
    } else {
      dispatch(
        setOrdersSelected(
          Object.fromEntries(
            returnableRows.map(row => [
              row.stableRowID,
              R.assoc('selected', true, row),
            ]),
          ),
        ),
      );
    }
  }

  return {
    computedRows,
    toggleAllRows,
    areAllSelected,
  };
}
